/**
 * Полосы плотности ленты. Одна функция на продукт, без DOM.
 *
 * Широкий стол (`room` / `desk`, контейнер ≥ 1000) визуально заморожен:
 * те же пять колонок, что в `next.css`. Адаптация начинается со `split`.
 * Ноль ширины — ещё не измерили, это не кулак: иначе jsdom и скрытый корень
 * вспыхнули бы `fist` на первом кадре.
 */

export type Band = 'room' | 'desk' | 'split' | 'tight' | 'fist';

export const BANDS: readonly Band[] = ['fist', 'tight', 'split', 'desk', 'room'];

export const FIST = 520;
export const TIGHT = 720;
export const SPLIT = 1000;
export const DESK = 1200;
export const HYSTERESIS = 24;
export const LEAVE_WIDE = SPLIT - HYSTERESIS;

const RANK: Record<Band, number> = {
  fist: 0,
  tight: 1,
  split: 2,
  desk: 3,
  room: 4,
};

export function isWide(band: Band): boolean {
  return band === 'room' || band === 'desk';
}

export function bandOf(px: number): Band {
  if (!Number.isFinite(px) || px <= 0) return 'room';
  if (px < FIST) return 'fist';
  if (px < TIGHT) return 'tight';
  if (px < SPLIT) return 'split';
  if (px < DESK) return 'desk';
  return 'room';
}

/**
 * Следующая полоса с гистерезисом на узких границах.
 *
 * В широкий стол входим сразу при ≥ 1000. Выходим только ниже 976, чтобы
 * окно на почти полном мониторе не схлопывалось каждым пикселем.
 * `room` ↔ `desk` без запаса: они выглядят одинаково.
 */
export function nextBand(px: number, current: Band | null): Band {
  const raw = bandOf(px);
  if (current === null || raw === current) return raw;
  if (isWide(current) && isWide(raw)) return raw;
  if (isWide(current) && !isWide(raw) && px >= LEAVE_WIDE) {
    return px >= DESK ? 'room' : 'desk';
  }
  if (!isWide(current) && isWide(raw)) return raw;

  const edges: ReadonlyArray<readonly [Band, Band, number]> = [
    ['fist', 'tight', FIST],
    ['tight', 'split', TIGHT],
  ];
  for (const [narrow, wide, edge] of edges) {
    if (current === narrow && raw === wide && px < edge + HYSTERESIS) return current;
    if (current === wide && raw === narrow && px >= edge - HYSTERESIS) return current;
  }
  return raw;
}

export function narrower(a: Band, b: Band): boolean {
  return RANK[a] < RANK[b];
}
